import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, Send, CheckCircle2, ArrowUpRight, Clock, Globe2 } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import { BRAND } from "../lib/constants";

export default function ContactPage() {
  const [data, setData] = useState({ name: "", email: "", company: "", message: "", interest: "Forex CRM" });
  const [done, setDone] = useState(false);

  const handle = (e) => setData({ ...data, [e.target.name]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!data.name || !data.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) || !data.message) {
      toast.error("Please fill name, valid email, and message.");
      return;
    }
    setDone(true);
    toast.success("Thanks — we'll get back within 24 hours.");
    setData({ name: "", email: "", company: "", message: "", interest: "Forex CRM" });
    setTimeout(() => setDone(false), 4000);
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        breadcrumb={[{ label: "Contact" }]}
        title={<>Let's <span className="text-gradient-cyan italic">talk shop.</span></>}
        lede="Tell us what you're trying to ship. We'll respond within one business day with either a plan, an honest 'not a fit', or pointers to someone better suited."
      />

      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 p-8 sm:p-10 rounded-3xl theme-panel border space-y-5"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="w-10 h-px bg-[#00C2FF]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#00C2FF] font-bold">
                / Inbound form
              </span>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 block mb-2">
                  Your name
                </label>
                <input
                  name="name" value={data.name} onChange={handle}
                  data-testid="contact-name"
                  className="theme-input border focus:border-[#00C2FF] outline-none rounded-xl px-4 py-3 text-sm"
                  placeholder="Jane Trader" required
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 block mb-2">
                  Email
                </label>
                <input
                  name="email" type="email" value={data.email} onChange={handle}
                  data-testid="contact-email"
                  className="theme-input border focus:border-[#00C2FF] outline-none rounded-xl px-4 py-3 text-sm"
                  placeholder="jane@broker.com" required
                />
              </div>
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 block mb-2">
                Company / desk
              </label>
              <input
                name="company" value={data.company} onChange={handle}
                data-testid="contact-company"
                className="theme-input border focus:border-[#00C2FF] outline-none rounded-xl px-4 py-3 text-sm"
                placeholder="ACME Markets Ltd."
              />
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 block mb-2">
                I'm interested in
              </label>
              <select
                name="interest" value={data.interest} onChange={handle}
                data-testid="contact-interest"
                className="theme-input border focus:border-[#00C2FF] outline-none rounded-xl px-4 py-3 text-sm"
              >
                {["Forex CRM", "Liquidity Bridge", "Prop Firm CRM", "Forex Copier", "Custom Tools", "Consulting", "Web / mobile build", "Just exploring"].map((o) => (
                  <option key={o} className="bg-[#000000]">{o}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 block mb-2">
                Tell us a bit more
              </label>
              <textarea
                name="message" value={data.message} onChange={handle}
                data-testid="contact-message"
                rows="5"
                className="theme-input border focus:border-[#00C2FF] outline-none rounded-xl px-4 py-3 text-sm resize-none"
                placeholder="What are you trying to launch / fix / scale?"
                required
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit"
              className="btn-cyan inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold w-full sm:w-auto"
            >
              {done ? (<><CheckCircle2 className="w-4 h-4" /> Sent</>) : (<>Send message <Send className="w-4 h-4" /></>)}
            </button>
          </motion.form>

          {/* Sidebar */}
          <div className="lg:col-span-5 space-y-5">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-7 rounded-3xl border border-white/10 bg-gradient-to-br from-[#001a2e] to-black"
            >
              <Clock className="w-6 h-6 text-[#00C2FF] mb-3" />
              <h3 className="font-display text-lg font-semibold mb-2">First response in &lt; 24h</h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                Inbox monitored Mon–Fri 09:00–22:00 UTC and Sat 12:00–18:00 UTC. Critical
                production issues for existing clients: 24/7 via dedicated Slack.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="p-7 rounded-3xl theme-panel border space-y-4"
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00C2FF]">/ Direct lines</div>
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 text-sm text-gray-200 hover:text-[#00C2FF] transition-colors">
                <Mail className="w-4 h-4 text-[#00C2FF]" /> {BRAND.email}
              </a>
              <a
                href={`tel:${BRAND.phone.replace(/[\s()-]/g, "")}`}
                className="flex items-center gap-3 text-sm text-gray-200 hover:text-[#00C2FF] transition-colors whitespace-nowrap"
              >
                <Phone className="w-4 h-4 text-[#00C2FF] shrink-0" /> {BRAND.phone}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="p-7 rounded-3xl border border-[#00C2FF]/30 bg-[#00C2FF]/5"
            >
              <Globe2 className="w-6 h-6 text-[#00C2FF] mb-3" />
              <h3 className="font-display text-lg font-semibold mb-2">Existing client?</h3>
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                Use your dedicated Slack channel or open a ticket inside the Trader's Room admin.
              </p>
              <a href={`mailto:support@${BRAND.domain}`} className="inline-flex items-center gap-2 text-sm text-[#00C2FF] hover:text-white transition-colors">
                Email support <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
